import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { NO_VALUE } from './values.exchange.provider';
import { debounceTime, filter } from 'rxjs/operators';

export interface UrlParamToWatch {
  name: string;
  mandatory: boolean;
  defaultValue?: any;
}

@Injectable({
  providedIn: 'root'
})
export class UrlListener {
  watchedParams = new Map<string, UrlParamToWatch>();
  queryParams = {};
  listeners = new Map<string, BehaviorSubject<any>>();
  returnUrl = false;

  constructor(private readonly activatedRoute: ActivatedRoute, private readonly router: Router) {
    this.router.events
      .pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
      .subscribe(() => {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this._processQueryParams(queryParams);
      });
    this.activatedRoute.queryParams.subscribe(changes => {
      const url = this.activatedRoute.snapshot.url;
      if (url.length === 0) {
        return;
      }
      this._processQueryParams(changes);
    });
  }

  _processQueryParams(changes) {
    const currentValues = new Map<string, any>();
    this.returnUrl = false; // assume this is already gone
    // important: if some parameter was completely removed from url, we should check whether
    // it was mentioned by anyone in queryParams - remove value from there and notify listener of that
    // now-gone query parameter about value change.
    if (changes) {
      for (const prop of Object.keys(changes)) {
        if (prop === 'returnUrl') {
          this.returnUrl = true;
          continue;
        }
        if (!this.watchedParams.has(prop)) {
          continue;
        }
        let value = changes[prop];
        if (value) {
          value = JSON.parse(value);
        } else {
          value = null;
        }
        currentValues.set(prop, value);
      }
    }
    // compare queryParams vs currentValues. If some keys are gone - we should still notify listeners about
    // value reset to nil - so that they can react to this.
    for (const knownParam of Object.keys(this.queryParams)) {
      if (!currentValues.has(knownParam)) {
        currentValues.set(knownParam, null);
      }
    }
    // it might be some 'watched' params are not yet initialized. do so now
    this.watchedParams.forEach((value, key) => {
      if (!currentValues.has(key) && value.mandatory) {
        currentValues.set(key, null);
      }
    });
    currentValues.forEach((value, key) => {
      this.notifyListeners(key, value);
    });
  }

  hasReturnUrl() {
    return this.returnUrl;
  }

  notifyListeners(topic: string, value: any) {
    let producer = this.listeners.get(topic);
    if (!producer) {
      producer = new BehaviorSubject<any>(value);
      this.listeners.set(topic, producer);
    }
    producer.next(value);
  }

  registerListener(topic: string, subscriber) {
    let producer = this.listeners.get(topic);
    if (!producer) {
      producer = new BehaviorSubject<any>(NO_VALUE);
      this.listeners.set(topic, producer);
    }
    if (producer) {
      return producer.pipe(debounceTime(0)).subscribe(subscriber);
    }
  }

  declareUrlParam(param: UrlParamToWatch) {
    this.watchedParams.set(param.name, param);
  }

  updateUrlParam(topic: string, values: any) {
    this.updateMultipleUrlParams({
      [topic]: values
    });
  }

  updateMultipleUrlParams(multiple: {} = {}) {
    const queryParams = {};

    for (const topic of Object.keys(multiple)) {
      queryParams[topic] = JSON.stringify(multiple[topic]);
      this.queryParams[topic] = 'true'; //not interested in value. just in mere fact we have touched it
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: true
    });
  }
}
